/**
 * Roof Master — EditableCell (Ship 1 of the editable-grid build).
 *
 * Generic click-to-edit cell driven by a ColumnDef. Click a cell → it swaps to
 * the right input for its type; blur or Enter commits, Esc cancels. Saves go
 * straight to `deals` (single source of truth), optimistic with revert-on-error.
 * Click-to-edit (vs. always-rendered inputs) keeps the DOM light across ~35
 * columns × many rows and gives the spreadsheet feel.
 *
 * Booleans render as an always-on checkbox (cheap, no edit mode).
 * Special columns (DealLink, status→stage, adjuster-date, RS) are NOT routed
 * here — RoofMasterTable renders those itself.
 */
import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { ColumnDef, SelectOption } from "./roofMasterColumns";
import { utcIsoToLocalInput, localInputToUtcIso } from "./roofMasterColumns";

interface EditableCellProps {
  dealId: string;
  column: ColumnDef;
  value: unknown;
  /** Resolved options for `select` cells (overrides column.options — e.g. roster). */
  options?: SelectOption[];
  /** Bump parent reload after a successful save. */
  onSaved?: () => void;
}

/* value → string for an <input>/<select> */
function toInputString(value: unknown, type: ColumnDef["type"]): string {
  if (value == null) return "";
  if (type === "datetime") return utcIsoToLocalInput(String(value));
  if (type === "date") return String(value).slice(0, 10);
  return String(value);
}

/* value → display text (read mode) */
function toDisplay(value: unknown, column: ColumnDef): string {
  if (value == null || value === "") return "";
  switch (column.type) {
    case "currency": {
      const n = Number(value);
      return Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
    }
    case "number":
      return String(value);
    case "date":
      return fmtDate(String(value));
    case "datetime":
      return fmtDateTime(String(value));
    case "select": {
      const opt = column.options?.find((o) => o.value === String(value));
      return opt?.label ?? String(value);
    }
    default:
      return String(value);
  }
}

/* YYYY-MM-DD → M/D/YY without constructing a Date (no TZ shift). */
function fmtDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}`;
}
/* fake-UTC ISO → M/D h:mma from raw digits (no Date construction). */
function fmtDateTime(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return iso;
  let h = Number(m[4]);
  const ap = h >= 12 ? "p" : "a";
  h = h % 12 || 12;
  return `${Number(m[2])}/${Number(m[3])} ${h}:${m[5]}${ap}`;
}

/* draft string → value to persist */
function normalize(draft: string, type: ColumnDef["type"]): unknown {
  const t = draft.trim();
  if (t === "") return null;
  switch (type) {
    case "number":
    case "currency": {
      const n = Number(t.replace(/[$,]/g, ""));
      return Number.isFinite(n) ? n : null;
    }
    case "datetime":
      return localInputToUtcIso(draft);
    default:
      return t; // text, date, select
  }
}

export default function EditableCell({ dealId, column, value, options, onSaved }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  // Set true by Esc so the trailing blur exits without writing.
  const cancelEditRef = useRef(false);

  // Focus the input when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement && (column.type === "text" || column.type === "number" || column.type === "currency")) {
        inputRef.current.select();
      }
    }
  }, [editing, column.type]);

  const opts = options ?? column.options ?? [];

  async function persist(next: unknown) {
    setSaving(true);
    setErr(false);
    try {
      // Custom fields live in deals.custom_fields — written atomically per-key
      // via RPC so concurrent edits to other custom fields don't clobber.
      const { error } = column.custom && column.cfKey
        ? await supabase.rpc("set_deal_custom_field", { p_deal_id: dealId, p_key: column.cfKey, p_value: next ?? null })
        : await supabase.from("deals").update({ [column.key]: next }).eq("id", dealId);
      if (error) throw error;
      onSaved?.();
    } catch {
      setErr(true);
    } finally {
      setSaving(false);
    }
  }

  /* ── Boolean: always-on checkbox, immediate save ── */
  if (column.type === "boolean") {
    return (
      <input
        type="checkbox"
        checked={value === true}
        disabled={!column.editable || saving}
        onChange={(e) => persist(e.target.checked)}
        className={"cursor-pointer " + (err ? "outline outline-1 outline-red-400" : "")}
        title={err ? "Save failed — try again" : column.hint}
      />
    );
  }

  /* ── Read-only / non-editable ── */
  if (!column.editable || column.type === "readonly") {
    return <span className="text-slate-600">{toDisplay(value, column)}</span>;
  }

  /* ── Edit mode ── */
  if (editing) {
    // Single commit path. Esc sets cancelEditRef so the trailing blur exits
    // without writing; Enter blurs the field so blur is the only writer.
    const finishCommit = (raw: string) => {
      setEditing(false);
      if (cancelEditRef.current) { cancelEditRef.current = false; return; }
      if (raw === toInputString(value, column.type)) return; // unchanged
      void persist(normalize(raw, column.type));
    };
    const common = "w-full border border-bardin-orange rounded px-1 py-0.5 text-[11px] bg-white";

    if (column.type === "select") {
      return (
        <select
          ref={(el) => { inputRef.current = el; }}
          defaultValue={toInputString(value, column.type)}
          onChange={(e) => finishCommit(e.target.value)} // pick = commit
          onBlur={() => setEditing(false)} // exit without writing (onChange already saved any change)
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
          className={common + " cursor-pointer"}
        >
          <option value="">—</option>
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }

    const inputType = column.type === "date" ? "date" : column.type === "datetime" ? "datetime-local" : "text";
    return (
      <input
        ref={(el) => { inputRef.current = el; }}
        type={inputType}
        inputMode={column.type === "number" || column.type === "currency" ? "decimal" : undefined}
        defaultValue={toInputString(value, column.type)}
        onBlur={(e) => finishCommit(e.target.value)} // the single writer
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          else if (e.key === "Escape") { cancelEditRef.current = true; (e.target as HTMLInputElement).blur(); }
        }}
        className={common}
      />
    );
  }

  /* ── Read mode (click to edit) ── */
  const display = toDisplay(value, column);
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={
        "w-full text-left truncate px-0.5 py-0.5 rounded hover:bg-orange-50 hover:ring-1 hover:ring-orange-200 " +
        (display ? "text-slate-700" : "text-slate-300") +
        (saving ? " opacity-50" : "") +
        (err ? " ring-1 ring-red-400" : "")
      }
      title={err ? "Save failed — click to retry" : display || column.hint || "Click to edit"}
    >
      {display || "—"}
      {saving && <span className="ml-1 text-[9px] text-slate-400">…</span>}
    </button>
  );
}
